import React from 'react';
import { Card, Button, ProgressBar, Badge, Spinner, Alert } from 'react-bootstrap';

const SubscriptionStatusCard = ({ subscription, currentUserRole, onSyncUsage, isSyncing, navigate }) => {
  // Garante que a assinatura e o plano existam antes de renderizar.
  if (!subscription || !subscription.plan) {
    return null;
  }

  const { plan, status, current_month_appointments, current_period_ends_at } = subscription;
  // Unifica a obtenção do limite de agendamentos.
  // Prioriza o `total_appointment_limit` que já vem somado do backend.
  // Mantém os outros como fallback para o plano de teste ou outras situações.
  const limit = plan?.total_appointment_limit ?? plan?.appointment_limit ?? plan?.limit_appointments;

  // Formata a data de vencimento para o padrão brasileiro, tratando questões de fuso horário.
  const formattedDate = current_period_ends_at
    ? new Date(current_period_ends_at + 'T00:00:00').toLocaleDateString('pt-BR', { timeZone: 'UTC' })
    : null;

  // Adiciona uma verificação para identificar se o plano atual é gratuito.
  const isFreePlan = plan.price === 0;


  const usagePercentage = limit > 0 ? (current_month_appointments / limit) * 100 : 0;

  const getStatusVariant = () => {
    switch (status) {
      case 'active':
      case 'trialing':
        return 'success';
      case 'past_due':
        return 'warning';
      case 'canceled':
      case 'expired':
      case 'failed':
        return 'danger';
      default:
        return 'secondary';
    }
  };

  const getStatusText = () => {
    switch (status) {
      case 'active': return 'Ativa';
      case 'trialing': return 'Período de Teste';
      case 'past_due': return 'Pendente';
      case 'canceled': return 'Cancelada';
      case 'expired': return 'Expirada';
      case 'failed': return 'Pagamento Falhou';
      default: return status;
    }
  };

  return (
    <Card className="h-100 shadow-sm">
      <Card.Header>
        <Card.Title as="h5">
          Status da Assinatura
        </Card.Title>
      </Card.Header>
      <Card.Body className="d-flex flex-column">
        <div className="d-flex justify-content-between align-items-start mb-3">
          <div>
            <h6 className="mb-0">Seu Plano Atual</h6>
            <span className="text-primary fw-bold fs-5">{plan?.name || 'Não definido'}</span>
            {/* NOVO: Exibe a data de vencimento para planos ativos ou pendentes */}
            {(status === 'active' || status === 'past_due') && formattedDate && (
              <small className="text-muted d-block mt-1">
                Próximo ciclo em: <strong>{formattedDate}</strong>
              </small>
            )}
          </div>
          <Badge bg={getStatusVariant()} pill className="px-3 py-2">
            {getStatusText()}
          </Badge>
        </div>

        {status === 'trialing' && (
          <p className="text-muted small">
            <strong>Você está em seu período de teste gratuito.</strong>
          </p>
        )}

        {status === 'failed' && (
          <Alert variant="danger" className="mt-2 small">
            <strong>Houve um problema com seu pagamento.</strong>
            <p className="mb-0">
              Não foi possível processar a cobrança no cartão fornecido. Por favor, 
              tente novamente com outro plano ou cartão.
            </p>
          </Alert>
        )}

        {plan.appointment_limit != -1 && (
          <div className="mt-2">
            <div className="d-flex justify-content-between">
              <h6 className="mb-1">Uso de Créditos</h6>
              <small>{`${current_month_appointments || 0} / ${limit || '∞'}`}</small>
            </div>
            <ProgressBar 
              now={usagePercentage} 
              variant={usagePercentage > 90 ? 'danger' : usagePercentage > 75 ? 'warning' : 'success'}
              className="mb-1"
            />
            <div className="text-end">
              <Button variant="link" size="sm" onClick={onSyncUsage} disabled={isSyncing}>
                {isSyncing ? <Spinner as="span" animation="border" size="sm" /> : 'Sincronizar Uso'}
              </Button>
            </div>
          </div>

        )}
        

        <div className="mt-auto pt-3">
          <div className="d-grid gap-2">
            {/* Botão para comprar créditos extras, visível para planos gratuitos ou quando o uso está alto, e quando não for plano Vitalício */}
            {status === 'active' && (isFreePlan || usagePercentage > 75) && plan.appointment_limit != -1 && (
              <Button 
                variant="outline-primary" 
                onClick={() => navigate('/comprar-creditos')}
              >
                Comprar Créditos Extras
              </Button>
            )}

            {/* Botão principal de ação, agora também visível para planos gratuitos */}
            {(status === 'trialing' || status === 'failed' || (status === 'active' && isFreePlan)) && (
                <Button 
                  variant={
                    status === 'failed' ? 'danger' 
                    : status === 'trialing' ? 'success' 
                    : 'primary' // 'primary' para o botão "Alterar Plano"
                  } 
                  onClick={() => navigate('/pricing')}
                >
                  {status === 'failed' ? 'Tentar Pagamento Novamente' 
                   : status === 'trialing' ? 'Contratar um Plano Agora' 
                   : 'Alterar Plano'}
                </Button>
            )}
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};

export default SubscriptionStatusCard;